(() => {
  const root = document.querySelector('[data-product-video-popup]');

  if (!root || root.dataset.initialized === 'true') return;

  root.dataset.initialized = 'true';

  const video = root.querySelector('[data-product-video]');
  const media = root.querySelector('[data-product-video-media]');
  const dismissButton = root.querySelector('[data-product-video-dismiss]');
  const collapseButton = root.querySelector('[data-product-video-collapse]');
  const overlay = root.querySelector('[data-product-video-overlay]');
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const productId = root.dataset.productId || 'unknown';
  const sessionKey = `gpProductVideoPopupClosed:v2:${productId}`;
  let sourcesLoaded = false;
  let lastFocus = null;
  let collisionFrame = 0;
  let resumeSiteMusicAfterClose = false;

  const isEnabledForViewport = () => {
    if (mobileQuery.matches) return !root.classList.contains('gp-product-video-popup--mobile-disabled');
    return !root.classList.contains('gp-product-video-popup--desktop-disabled');
  };

  const wasDismissed = () => {
    try {
      return sessionStorage.getItem(sessionKey) === 'true';
    } catch (error) {
      return false;
    }
  };

  const rememberDismissal = () => {
    try {
      sessionStorage.setItem(sessionKey, 'true');
    } catch (error) {
      // Storage can be unavailable in strict privacy modes; closing still works.
    }
  };

  const loadSources = () => {
    if (sourcesLoaded) return;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });
    video.preload = 'metadata';
    video.load();
    sourcesLoaded = true;
  };

  const rectanglesOverlap = (first, second) => (
    first.left < second.right &&
    first.right > second.left &&
    first.top < second.bottom &&
    first.bottom > second.top
  );

  const readPixelValue = (styles, property, fallback = 0) => {
    const value = Number.parseFloat(styles.getPropertyValue(property));
    return Number.isFinite(value) ? value : fallback;
  };

  const updateSafePosition = () => {
    collisionFrame = 0;
    if (!root.classList.contains('is-visible') || root.classList.contains('is-expanded')) return;

    const styles = getComputedStyle(root);
    const isMobile = mobileQuery.matches;
    const configuredBottom = isMobile
      ? readPixelValue(styles, '--gp-pvp-mobile-bottom', 90) + readPixelValue(styles, '--gp-pvp-safe-area-bottom')
      : 24;
    const rootRect = root.getBoundingClientRect();
    const width = rootRect.width;
    const height = rootRect.height;
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportHeight = viewport?.height || window.innerHeight;
    const viewportBottom = viewportTop + viewportHeight;
    const viewportWidth = viewport?.width || window.innerWidth;
    const left = root.classList.contains('gp-product-video-popup--left')
      ? Number.parseFloat(styles.left) || 12
      : window.innerWidth - (Number.parseFloat(styles.right) || 12) - width;
    let safeBottom = configuredBottom;

    const candidates = document.querySelectorAll(
      '[data-sticky-add-to-cart], [class*="sticky"], [id*="sticky"], [class*="chat"], [id*="chat"], iframe, .gp-cart-link, .gp-sound-toggle'
    );

    for (let pass = 0; pass < 3; pass += 1) {
      const popupRect = {
        left,
        right: left + width,
        top: viewportBottom - safeBottom - height,
        bottom: viewportBottom - safeBottom
      };
      let nextBottom = safeBottom;

      candidates.forEach((candidate) => {
        if (root.contains(candidate)) return;

        const rect = candidate.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;

        // Full-page app/preview frames are not bottom controls. Treating them as
        // collisions can push a compact popup completely above a phone viewport.
        if (rect.width >= viewportWidth * 0.6 && rect.height >= viewportHeight * 0.6) return;

        const position = getComputedStyle(candidate).position;
        if (position !== 'fixed' && position !== 'sticky') return;

        if (rectanglesOverlap(popupRect, rect)) {
          nextBottom = Math.max(nextBottom, viewportBottom - rect.top + 12);
        }
      });

      if (nextBottom === safeBottom) break;
      safeBottom = nextBottom;
    }

    if (isMobile) {
      const maximumBottom = Math.max(12, viewportHeight - height - 12);
      safeBottom = Math.min(Math.max(configuredBottom, safeBottom), maximumBottom);
    }

    root.style.setProperty('--gp-pvp-safe-bottom', `${safeBottom}px`);

  };

  const scheduleSafePosition = () => {
    if (collisionFrame) return;
    collisionFrame = requestAnimationFrame(updateSafePosition);
  };

  const show = () => {
    if (wasDismissed() || !isEnabledForViewport()) return;

    loadSources();
    root.hidden = false;
    requestAnimationFrame(() => {
      root.classList.add('is-visible');
      updateSafePosition();
      video.muted = true;
      video.play().catch(() => {
        // Muted autoplay can still be blocked by browser or user preferences.
      });
    });
  };

  const pauseSiteMusic = () => {
    if (typeof window.gpSiteAudio?.pauseForProductVideo === 'function') {
      return window.gpSiteAudio.pauseForProductVideo();
    }

    const siteMusic = document.querySelector('[data-gp-site-music]');
    const wasPlaying = Boolean(siteMusic && !siteMusic.paused && !siteMusic.ended);
    if (wasPlaying) siteMusic.pause();
    return wasPlaying;
  };

  const restoreSiteMusic = () => {
    const shouldResume = resumeSiteMusicAfterClose;
    resumeSiteMusicAfterClose = false;
    if (!shouldResume) return;

    if (typeof window.gpSiteAudio?.resumeAfterProductVideo === 'function') {
      window.gpSiteAudio.resumeAfterProductVideo(true);
      return;
    }

    document.querySelector('[data-gp-site-music]')?.play().catch(() => {});
  };

  const collapse = () => {
    if (!root.classList.contains('is-expanded')) return;

    root.classList.remove('is-expanded');
    document.documentElement.classList.remove('gp-pvp-modal-open');
    video.controls = false;
    video.muted = true;
    media.setAttribute('role', 'button');
    media.setAttribute('tabindex', '0');
    media.setAttribute('aria-label', 'Expand product video');
    overlay.setAttribute('tabindex', '-1');
    updateSafePosition();
    video.play().catch(() => {});
    restoreSiteMusic();
    lastFocus?.focus?.({ preventScroll: true });
  };

  const expand = () => {
    if (!root.classList.contains('is-visible') || root.classList.contains('is-expanded')) return;

    lastFocus = document.activeElement;
    resumeSiteMusicAfterClose = pauseSiteMusic();
    root.classList.add('is-expanded');
    document.documentElement.classList.add('gp-pvp-modal-open');
    video.controls = true;
    media.removeAttribute('role');
    media.removeAttribute('tabindex');
    media.removeAttribute('aria-label');
    overlay.removeAttribute('tabindex');
    video.muted = false;
    video.play().catch(() => {});
    collapseButton.focus({ preventScroll: true });
  };

  const dismiss = () => {
    rememberDismissal();
    const wasExpanded = root.classList.contains('is-expanded');
    if (wasExpanded) {
      video.muted = true;
      restoreSiteMusic();
    }
    document.documentElement.classList.remove('gp-pvp-modal-open');
    root.classList.remove('is-expanded', 'is-visible');
    root.classList.add('is-dismissing');
    video.pause();
    window.setTimeout(() => {
      root.hidden = true;
      root.classList.remove('is-dismissing');
      video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      video.load();
      sourcesLoaded = false;
    }, 460);
  };

  dismissButton.addEventListener('click', dismiss);
  collapseButton.addEventListener('click', collapse);
  overlay.addEventListener('click', collapse);
  media.addEventListener('click', () => {
    if (!root.classList.contains('is-expanded')) expand();
  });
  media.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      expand();
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.classList.contains('is-expanded')) collapse();
  });
  window.addEventListener('resize', scheduleSafePosition, { passive: true });
  window.addEventListener('scroll', scheduleSafePosition, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleSafePosition, { passive: true });
  window.visualViewport?.addEventListener('scroll', scheduleSafePosition, { passive: true });
  mobileQuery.addEventListener?.('change', () => {
    if (!isEnabledForViewport()) {
      collapse();
      root.classList.remove('is-visible');
      video.pause();
      root.hidden = true;
      return;
    }
    if (!wasDismissed()) show();
  });

  if (wasDismissed() || !isEnabledForViewport()) return;

  const delay = Math.max(0, Number.parseInt(root.dataset.delay, 10) || 0);
  window.setTimeout(show, delay);
})();
